import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navigation from "../components/Navigation";
import Seo, { breadcrumbs } from "../components/Seo";
import { MetaLabel, CornerTick } from "../brand/Brand";
import { subscribePublishedCaseStudies } from "../caseStudies/publicApi";
import { STATIC_ROUTES } from "../lib/seo";
import "./Sitemap.css";

/**
 * Human-readable index of every public URL.
 *
 * This is the companion to /sitemap.xml, not a replacement: the XML file is
 * for crawlers, this page is for people — and, incidentally, for crawlers
 * that arrive via a link rather than a submitted sitemap. Because it links
 * every case study and post from a single crawlable page, it also flattens
 * crawl depth: nothing on the site is more than two clicks from the root.
 */

const GROUP_ORDER = ["Studio", "Legal"];

const SitemapSection = ({ title, count, children }) => (
  <section className="sm-section">
    <div className="sm-section-head">
      <MetaLabel>{title}</MetaLabel>
      <MetaLabel>({String(count).padStart(2, "0")})</MetaLabel>
    </div>
    <ul className="sm-list">{children}</ul>
  </section>
);

const SitemapLink = ({ to, label, description }) => (
  <li className="sm-item">
    <Link to={to} className="sm-link">
      <span className="sm-link-label">{label}</span>
      <span className="sm-link-path">{to}</span>
    </Link>
    {description && <p className="sm-link-desc">{description}</p>}
  </li>
);

const Sitemap = () => {
  const [cases, setCases] = useState([]);
  const [posts, setPosts] = useState([]);

  useEffect(() => subscribePublishedCaseStudies(setCases), []);

  // The blog listing loads Firestore lazily to keep it out of the initial
  // bundle; mirror that here rather than pulling the SDK in eagerly.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { collection, getDocs, orderBy, query, where } = await import(
          "firebase/firestore"
        );
        const { db } = await import("../firebase");
        const snap = await getDocs(
          query(
            collection(db, "blog"),
            where("status", "==", "live"),
            orderBy("date", "desc")
          )
        );
        if (!alive) return;
        setPosts(
          snap.docs.map((d) => {
            const data = d.data();
            return {
              id: d.id,
              title: data.title,
              category: data.category,
              date: data.date?.toDate
                ? data.date.toDate().toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "short",
                    day: "numeric",
                  })
                : data.date,
            };
          })
        );
      } catch (err) {
        // A sitemap that renders its static half beats one that renders none.
        console.warn("[sitemap] blog list unavailable", err);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const groups = GROUP_ORDER.map((group) => ({
    group,
    routes: STATIC_ROUTES.filter((r) => r.group === group),
  })).filter((g) => g.routes.length);

  const total = STATIC_ROUTES.length + cases.length + posts.length;

  return (
    <div className="sm-page">
      <Seo
        title="Sitemap — bboy creative"
        description="Every public page on bboycreative.com — the studio, case studies, blog posts, and legal policies — in one index."
        path="/sitemap"
        jsonLd={breadcrumbs([
          { name: "Home", path: "/" },
          { name: "Sitemap", path: "/sitemap" },
        ])}
      />

      <Navigation />

      <header className="sm-header">
        <div className="sm-container sm-frame">
          <CornerTick pos="tl" />
          <CornerTick pos="tr" />
          <CornerTick pos="bl" />
          <CornerTick pos="br" />

          <div className="sm-header-meta">
            <MetaLabel>Index / Sitemap</MetaLabel>
            <MetaLabel>({String(total).padStart(2, "0")}) pages</MetaLabel>
          </div>

          <h1 className="sm-h1">Everything, in one place.</h1>
          <p className="sm-sub">
            A complete index of the public site. Machine-readable version at{" "}
            <a href="/sitemap.xml" className="sm-inline-link">
              /sitemap.xml
            </a>
            .
          </p>
        </div>
      </header>

      <main className="sm-container sm-main">
        {groups.map(({ group, routes }) => (
          <SitemapSection key={group} title={group} count={routes.length}>
            {routes.map((route) => (
              <SitemapLink
                key={route.path}
                to={route.path}
                label={route.label}
                description={route.description}
              />
            ))}
          </SitemapSection>
        ))}

        <SitemapSection title="Case studies" count={cases.length}>
          {cases.length === 0 && (
            <li className="sm-empty">No published case studies yet.</li>
          )}
          {cases.map((cs) => (
            <SitemapLink
              key={cs.id}
              to={`/work/${cs.id}`}
              label={cs.title}
              description={[cs.client, cs.year].filter(Boolean).join(" · ")}
            />
          ))}
        </SitemapSection>

        <SitemapSection title="Blog" count={posts.length}>
          {posts.length === 0 && (
            <li className="sm-empty">No published posts yet.</li>
          )}
          {posts.map((post) => (
            <SitemapLink
              key={post.id}
              to={`/blog/${post.id}`}
              label={post.title}
              description={[post.category, post.date]
                .filter(Boolean)
                .join(" · ")}
            />
          ))}
        </SitemapSection>
      </main>
    </div>
  );
};

export default Sitemap;
